import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "../mock";
import { WhatsAppIcon, waUrl } from "./WhatsAppFab";
import { subscribeApi } from "../api";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setStatus("loading");
    try {
      await subscribeApi({ email, source: "footer" });
      setStatus("done");
      setEmail("");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Could not subscribe. Try again.");
    }
  };

  return (
    <footer className="bg-[var(--espresso)] text-[var(--cream)] mt-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Studio info */}
          <div className="md:col-span-5">
            <img src={LOGO_URL} alt="Venus 3D Creations" className="h-10 w-auto bg-[var(--cream)] p-1.5 rounded-sm" />
            <div className="mt-6 text-sm text-[var(--cream)]/60 leading-[1.9] space-y-1 tracking-wide">
              <p>Ahmedabad, India</p>
              <p><a href="mailto:venus3dcreations@gmail.com" className="hover:text-[var(--cream)]">venus3dcreations@gmail.com</a></p>
              <p><a href="tel:+918849440828" className="hover:text-[var(--cream)]">+91 88494 40828</a></p>
              <p className="pt-2 text-[11px] tracking-[0.2em] uppercase text-[var(--cream)]/35">
                GSTIN · 24DIZPP3171F1ZT
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <a href="https://www.instagram.com/venus3dcreations" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-[var(--cream)]/15 flex items-center justify-center hover:border-[var(--cream)]/40 transition-colors">
                <Instagram className="w-4 h-4" strokeWidth={1.25} />
              </a>
              <a href="https://www.facebook.com/venus3dcreations" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-[var(--cream)]/15 flex items-center justify-center hover:border-[var(--cream)]/40 transition-colors">
                <Facebook className="w-4 h-4" strokeWidth={1.25} />
              </a>
              <a href={waUrl("Hi Venus! A quick question about your lamps —")} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full border border-[var(--cream)]/15 flex items-center justify-center hover:border-[var(--cream)]/40 hover:text-[#25D366] transition-colors">
                <WhatsAppIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--copper-light)] mb-5">Studio</p>
            <ul className="space-y-3 text-sm text-[var(--cream)]/70">
              <li><Link to="/shop" className="vc-link-underline">Shop</Link></li>
              <li><Link to="/about" className="vc-link-underline">About</Link></li>
              <li><Link to="/contact" className="vc-link-underline">Contact</Link></li>
              <li><Link to="/policies/shipping" className="vc-link-underline">Shipping</Link></li>
              <li><Link to="/policies/refunds" className="vc-link-underline">Refunds</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--copper-light)] mb-5">Letters from the studio</p>
            <p className="text-sm text-[var(--cream)]/60 leading-[1.9] mb-5">
              New launches, limited drops and the occasional behind-the-scenes note — never more than once a month.
            </p>
            {status === "done" ? (
              <div data-testid="subscribe-success" className="flex items-center gap-2 text-sm text-[var(--copper-light)]">
                <Check className="w-4 h-4" /> Thank you — you're on the list.
              </div>
            ) : (
              <form onSubmit={onSubscribe} data-testid="footer-subscribe-form" className="flex">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  aria-label="Email address"
                  data-testid="footer-subscribe-email"
                  className="flex-1 bg-transparent border border-[var(--cream)]/20 border-r-0 h-11 px-4 text-sm text-[var(--cream)] placeholder:text-[var(--cream)]/35 focus:outline-none focus:border-[var(--copper-light)]"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="footer-subscribe-submit"
                  className="h-11 px-5 bg-[var(--copper)] text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[var(--copper-dark)] transition-colors flex items-center justify-center min-w-[90px]"
                >
                  {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Payment + legal strip */}
        <div className="border-t border-[var(--cream)]/10 mt-12 pt-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--cream)]/40 mb-3">We accept</p>
              <div className="flex flex-wrap items-center gap-2">
                {["UPI", "Visa", "Mastercard", "RuPay", "Netbanking", "Razorpay", "Cash on Delivery"].map((pm) => (
                  <span
                    key={pm}
                    data-testid={`pay-badge-${pm.toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-[var(--cream)]/15 text-[var(--cream)]/70 rounded-sm"
                  >
                    {pm}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-[var(--cream)]/40 mt-3 leading-relaxed">
                Secure checkout powered by Razorpay. Cash on Delivery available across India.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[var(--cream)]/10 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-[var(--cream)]/40">
          <p>© {new Date().getFullYear()} Venus 3D Creations</p>
          <div className="flex gap-6">
            <Link to="/policies/privacy" className="hover:text-[var(--cream)]/70">Privacy</Link>
            <Link to="/policies/terms" className="hover:text-[var(--cream)]/70">Terms</Link>
            <Link to="/policies/refunds" className="hover:text-[var(--cream)]/70">Refunds</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
